/**
 * Calculated-by / checked-by workflow (CLAUDE.md 2.2).
 *
 * `submit -> check -> approve`, with `reject` returning to `none`. This is a
 * professional-responsibility mechanism, not a workflow nicety: only an approved
 * position may be issued FOR CONSTRUCTION, and any edit after a check resets the
 * state and marks the position stale.
 *
 * The buttons offer only the transition the current state permits. The server enforces
 * the same rules -- including self-check, which depends on the workspace -- so a
 * refusal here is a courtesy and a refusal there is the guarantee.
 */
import { useState } from 'react'
import { CheckCheck, CheckCircle2, Send, Undo2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/client'
import { useReviewPosition } from '@/api/hooks'
import type { Position } from '@/api/types'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card'
import { useToast } from '@/components/ui/toast'

export function ReviewControls({ position }: { position: Position }) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const review = useReviewPosition(position.id)
  const [note, setNote] = useState('')

  const run = async (action: 'submit' | 'check' | 'approve' | 'reject') => {
    try {
      await review.mutateAsync({ action, note: note || undefined })
      setNote('')
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error instanceof ApiError ? error.localised : undefined,
        variant: 'error',
      })
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('review.state')}</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3">
        {position.review_state === 'none' && (
          <Button onClick={() => run('submit')} loading={review.isPending} className="w-full">
            <Send size={15} aria-hidden />
            {t('review.submit')}
          </Button>
        )}

        {position.review_state === 'submitted' && (
          <>
            <div>
              <label htmlFor="review-note" className="mb-1 block text-sm text-text-muted">
                {t('review.note')}
              </label>
              <textarea
                id="review-note"
                value={note}
                onChange={(event) => setNote(event.target.value)}
                rows={2}
                className="w-full rounded-md border border-border bg-surface p-2 text-sm text-text"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={() => run('check')} loading={review.isPending} className="flex-1">
                <CheckCircle2 size={15} aria-hidden />
                {t('review.check')}
              </Button>
              <Button variant="ghost" onClick={() => run('reject')}>
                <Undo2 size={15} aria-hidden />
                {t('review.reject')}
              </Button>
            </div>
          </>
        )}

        {position.review_state === 'checked' && (
          <div className="flex gap-2">
            <Button
              variant="primary"
              onClick={() => run('approve')}
              loading={review.isPending}
              className="flex-1"
            >
              <CheckCheck size={15} aria-hidden />
              {t('review.approve')}
            </Button>
            <Button variant="ghost" onClick={() => run('reject')}>
              <Undo2 size={15} aria-hidden />
              {t('review.reject')}
            </Button>
          </div>
        )}

        {position.review_state === 'approved' && (
          <p className="text-sm text-[var(--status-pass)]">{t('review.state.approved')}</p>
        )}
      </CardBody>
    </Card>
  )
}
